import { useEffect, useMemo, useState } from 'react';
import DefaultFilterInputLayout from '../DefaultFilterInputLayout';
import FilterActions from '../FilterActions';
import { useFilterContext } from '../../../../FilterContext';
import BaseInputErrors from '../../../../../../ui/layout/form/input/BaseInputErrors';
import { DropDownFilterLayoutType } from '../../../../DropDownFilter';
import { Option, Option as OptionType, TextInput } from 'framework/form';
import { Box } from '@mui/material';
import useDropDownFilterHelper from '../useDropDownFilterHelper';
import SelectLayout from './SelectLayout';

const filterByLabel = (option: { data: Option }, rawInput: string) => {
  if (!rawInput?.trim()) return true;
  const label = String(option.data?.label ?? '').toLowerCase();
  const input = rawInput.trim().toLowerCase();
  return label.includes(input);
};

const DropDownFilterInputLayout: DropDownFilterLayoutType = props => {
  const {
    label,
    options: initialOptions = [],
    errorMessages,
    multiple = false,
    value: initialValue = '',
    source,
    onResetCallback,
    onChangeCallback,
    hasFilterByLabel,
    isLoading = false,
  } = props;
  const { apply: applyFilters } = useFilterContext();
  const { isMenuOpened, getDisplayedValue, toggleMenu, closeMenu } = useDropDownFilterHelper({ source });
  const [selectedValues, setSelectedValues] = useState<OptionType[]>();
  const [shouldApplyFilter, setShouldApplyFilter] = useState(false);
  const [filterInputValue, setFilterInputValue] = useState('');
  const displayedValue = getDisplayedValue(selectedValues);
  const hasError = errorMessages && errorMessages.length > 0;
  const invalidClass = hasError ? 'is-invalid' : '';
  const filterInputSource = `${source}.filterInput`;

  const options = useMemo(
    () =>
      (initialOptions as OptionType[]).map(({ value, ...optionProps }) => ({
        ...optionProps,
        value: value ? String(value) : value,
      })),
    [initialOptions],
  );

  const filteredOptions = useMemo(() => {
    if (!hasFilterByLabel || !filterInputValue) {
      return options;
    }

    return options.filter(opt =>
      filterByLabel({ ...opt, data: opt } as OptionType & { data: OptionType }, filterInputValue),
    );
  }, [options, filterInputValue, hasFilterByLabel]);

  const isContainValue = (value: string, allValues: string | string[]) =>
    multiple ? allValues?.includes(value) : String(allValues) === value;

  useEffect(() => {
    if (!multiple && isMenuOpened && shouldApplyFilter) {
      closeMenu();
      applyFilters();
      setShouldApplyFilter(false);
    }

    setSelectedValues(options.filter(({ value }) => isContainValue(value as string, initialValue)));
    // Suppressed warnings because we only need to call useEffect callback if updated dropdown value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue, options, setShouldApplyFilter]);

  return (
    <DefaultFilterInputLayout
      label={label}
      isOpen={isMenuOpened}
      toggleMenu={toggleMenu}
      displayedValue={displayedValue}
      source={source}
      className={invalidClass}
      onResetFilterCallback={onResetCallback}
    >
      {hasFilterByLabel && (
        <Box sx={{ px: 2, py: 1, mb: '-20px' }}>
          <TextInput
            source={filterInputSource}
            label="Filter options"
            value={filterInputValue}
            onChangeCallback={v => setFilterInputValue(v[filterInputSource])}
          />
        </Box>
      )}
      <SelectLayout
        {...props}
        options={filteredOptions}
        selectedValues={selectedValues}
        isLoading={isLoading}
        onChangeCallback={(value: any) => {
          onChangeCallback(value);
          if (!multiple) {
            setShouldApplyFilter(true);
          }
        }}
      />

      <BaseInputErrors errorMessages={errorMessages} />
      {multiple && <FilterActions onChangeCallback={toggleMenu} onResetCallback={toggleMenu} />}
    </DefaultFilterInputLayout>
  );
};

export default DropDownFilterInputLayout;
